/**
 * TajwidExercisePopover — wraps a target word in a Popover that opens a
 * QCM (multiple choice question) for the Tajwīd rule attached to it.
 *
 * Visual states:
 *   • Idle (no answer yet)   → green ring around the word
 *   • Correct answer         → solid green
 *   • Wrong answer           → solid red
 *   • Non-target words       → rendered untouched (returned by parent)
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, ExternalLink } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Badge } from '../ui/badge';

const stateClasses = (answer) => {
  if (!answer) {
    return 'ring-1 ring-emerald-400/60 bg-emerald-400/5 hover:bg-emerald-400/15 cursor-pointer';
  }
  if (answer.correct) {
    return 'ring-1 ring-emerald-500 bg-emerald-500/15 text-emerald-200';
  }
  return 'ring-1 ring-rose-500 bg-rose-500/15 text-rose-200';
};

export const TajwidExercisePopover = ({
  children,           // the rendered word content (TajwidColoredText output or plain text)
  target,             // { ayahKey, wordIndex, rule }
  answer,             // { chosen, correct, rule } | null
  choices,            // [{ rule, label_fr, color, harakat }]
  onChoose,           // (chosenRule) => void
  ariaLabel,
  testIdSuffix,
}) => {
  const [open, setOpen] = useState(false);
  if (!target) return children; // safety

  const correctLabel = choices.find((c) => c.rule === target.rule)?.label_fr || target.rule;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          aria-label={ariaLabel}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          className={[
            'inline-block rounded px-1 py-0.5 transition-colors duration-150',
            stateClasses(answer),
          ].join(' ')}
          data-testid={`tajwid-exercise-word-${testIdSuffix}`}
          data-answered={answer ? (answer.correct ? 'correct' : 'wrong') : 'idle'}
        >
          {children}
        </span>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        className="w-72 p-3 bg-surface-elevated border-border"
        data-testid="tajwid-exercise-popover"
      >
        {!answer && (
          <>
            <p className="text-xs text-text-muted mb-2">
              Quelle règle s'applique à ce mot ?
            </p>
            <div className="grid grid-cols-1 gap-2">
              {choices.map((c) => (
                <button
                  key={c.rule}
                  type="button"
                  onClick={() => {
                    onChoose(c.rule);
                  }}
                  className="text-left text-sm px-3 py-2 rounded-md border border-border bg-background/40 hover:bg-primary/10 hover:border-primary/40 transition-colors"
                  data-testid={`tajwid-choice-${c.rule}`}
                >
                  <span className="inline-block w-2 h-2 rounded-full mr-2"
                        style={{ backgroundColor: c.color }} />
                  {c.label_fr}
                </button>
              ))}
            </div>
          </>
        )}

        {answer && (
          <div data-testid="tajwid-exercise-feedback">
            <div className={`flex items-center gap-2 text-sm font-medium mb-2 ${
              answer.correct ? 'text-emerald-300' : 'text-rose-300'
            }`}>
              {answer.correct ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
              {answer.correct ? 'Bonne réponse' : 'Réponse incorrecte'}
            </div>
            {!answer.correct && (
              <p className="text-xs text-text-secondary mb-2">
                La règle correcte était : <Badge variant="outline">{correctLabel}</Badge>
              </p>
            )}
            <Link
              to={`/cours/tajwid/${target.rule}`}
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              data-testid="tajwid-feedback-lesson-link"
              onClick={() => setOpen(false)}
            >
              Voir la leçon complète <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default TajwidExercisePopover;
